import {
  Button,
  FieldError,
  FieldLabel,
  RadioGroup,
  RadioGroupItem,
} from '@arcadiapower/design-system';
import { useCallback } from 'react';
import { useForm, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from '@client/components/form';
import { ContentSectionFullHeight, Footer } from '@client/components/connect-container';
import { parseErrorMessage } from '@client/config/errors';
import { Provider, MfaVerificationMethods, MfaVerificationMethod } from '@schema/schemas';
import { ty } from '@client/styles/typography';

// Create a composite ID from type and identifier to handle cases where
// multiple methods share the same identifier (ENTUC-6855)
const createCompositeId = (method: MfaVerificationMethod) => `${method.type}:${method.identifier}`;

const parseCompositeId = (compositeId: string) => {
  const [type, ...rest] = compositeId.split(':');
  return { type, identifier: rest.join(':') }; // rejoin in case identifier contains ':'
};

export type Props = {
  provider: Provider;
  mfaVerificationMethods: MfaVerificationMethods;
  onSubmit: ({
    selectedVerificationMethod,
  }: {
    selectedVerificationMethod: MfaVerificationMethod;
  }) => void;
  onProviderReselect?: () => void;
  pending: boolean;
  error?: unknown;
};

export const MfaVerificationMethodForm = ({
  provider,
  mfaVerificationMethods,
  onSubmit,
  onProviderReselect,
  pending,
  error,
}: Props): JSX.Element => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.mfaVerificationMethodForm' });

  const { control, handleSubmit } = useForm({
    defaultValues: { mfaVerificationMethodIdentifier: '' },
  });
  const { field, fieldState } = useController({
    name: 'mfaVerificationMethodIdentifier',
    control,
    rules: {
      required: t('validations.verificationMethodRequired'),
    },
  });

  const handleFormSubmit = useCallback(
    ({ mfaVerificationMethodIdentifier }: { mfaVerificationMethodIdentifier: string }) => {
      const { type, identifier } = parseCompositeId(mfaVerificationMethodIdentifier);
      const selectedVerificationMethod = mfaVerificationMethods.find(
        method => method.type === type && method.identifier === identifier
      );
      if (selectedVerificationMethod) onSubmit({ selectedVerificationMethod });
    },
    [onSubmit, mfaVerificationMethods]
  );

  return (
    <>
      <ContentSectionFullHeight centerContentVertically data-testid="mfa-verification-method-form">
        <p className={ty('default', 'secondary')} style={{ marginBottom: '32px' }}>
          {t('description', { providerName: provider.name })}
        </p>
        <h2 className={ty('heading700')} style={{ marginBottom: '12px' }}>
          {t('methodTitle')}
        </h2>
        <Form
          onSubmit={handleSubmit(data => handleFormSubmit(data))}
          id="mfaVerificationMethodForm"
        >
          <RadioGroup
            className="mb-3 gap-3"
            name={field.name}
            onValueChange={field.onChange}
            value={field.value || undefined}
          >
            {mfaVerificationMethods.map(method => {
              const { name, type } = method;
              const compositeId = createCompositeId(method);
              const radioId = `mfa-method-${compositeId}`;
              return (
                <div className="flex items-center gap-2" key={compositeId}>
                  <RadioGroupItem
                    aria-invalid={!!fieldState.error}
                    id={radioId}
                    value={compositeId}
                  />
                  <FieldLabel htmlFor={radioId}>
                    <span className={ty('paragraph400')}>
                      {t(`methodType.${type}`, { methodName: name })}
                    </span>
                  </FieldLabel>
                </div>
              );
            })}
          </RadioGroup>
          <p className={ty('paragraph400', 'secondary')} style={{ marginBottom: '32px' }}>
            {t('disclaimer')}
          </p>
          {!!onProviderReselect && (
            <>
              <p className={ty('paragraph400')}>{t('unableToAuthenticate')}</p>
              <p className={ty('paragraph400')}>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto rounded-sm p-0"
                  onClick={onProviderReselect}
                >
                  {t('selectUtility')}
                </Button>
              </p>
            </>
          )}
          {!!(error || fieldState.error?.message) && (
            <FieldError>{parseErrorMessage(error, fieldState.error?.message)}</FieldError>
          )}
        </Form>
      </ContentSectionFullHeight>
      <Footer>
        <Button
          form="mfaVerificationMethodForm"
          aria-label={t('submitButton')}
          type="submit"
          loading={pending}
          className="w-full"
          variant="inverse"
        >
          {t('submitButton')}
        </Button>
      </Footer>
    </>
  );
};
